package com.tovtalk.ui.screen.profile

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CameraAlt
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Person
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TextFieldDefaults
import com.tovtalk.ui.scaffold.ScreenScaffold
import com.tovtalk.ui.scaffold.TovTalkTopBar
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tovtalk.di.koinInject
import com.tovtalk.di.disposableKoinInject
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.repository.AppRepository
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.profile.ProfileViewModel
import com.tovtalk.ui.components.CenteredContentWrapper
import com.tovtalk.ui.components.TovTalkAsyncImage
import com.tovtalk.ui.platform.rememberProfilePhotoPicker
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider
import kotlinx.coroutines.launch

private enum class EditField { USERNAME, FULLNAME, BIO }

@Composable
fun EditProfileScreen(
    onNavigateBack: () -> Unit,
    onPhotoUploaded: ((Int) -> Unit)? = null,
    viewModel: ProfileViewModel = disposableKoinInject(),
    appRepository: AppRepository = koinInject()
) {
    val userState by viewModel.userState.collectAsState()
    val uploadState by viewModel.uploadState.collectAsState()
    val uploadProgress by viewModel.uploadProgress.collectAsState()
    val updateProfileState by viewModel.updateProfileState.collectAsState()
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }

    // Editable fields
    var fullname by remember { mutableStateOf("") }
    var bio by remember { mutableStateOf("") }
    var receiveMessages by remember { mutableStateOf(true) }

    // Original values for change detection
    var originalFullname by remember { mutableStateOf("") }
    var originalBio by remember { mutableStateOf("") }
    var originalReceiveMessages by remember { mutableStateOf(true) }

    // Username fields
    var username by remember { mutableStateOf("") }
    var originalUsername by remember { mutableStateOf("") }
    var canChangeUsername by remember { mutableStateOf(true) }
    var daysUntilUsernameChange by remember { mutableStateOf(0) }
    var usernameValidationError by remember { mutableStateOf<String?>(null) }

    // Editing state
    var editingField by remember { mutableStateOf<EditField?>(null) }

    val hasChanges = fullname != originalFullname ||
        bio != originalBio ||
        receiveMessages != originalReceiveMessages ||
        (canChangeUsername && username != originalUsername)

    val pickPhoto = rememberProfilePhotoPicker { bytes, filename ->
        bytes?.let { viewModel.uploadProfilePhoto(it, filename ?: "profile.jpg") }
    }

    // Load user on enter
    LaunchedEffect(Unit) {
        viewModel.loadUser(null)
    }

    // Sync state from loaded user
    LaunchedEffect(userState) {
        if (userState is UiState.Success) {
            val user = (userState as UiState.Success).data
            fullname = user.fullname
            bio = user.bio ?: ""
            receiveMessages = user.receiveMessage
            originalFullname = user.fullname
            originalBio = user.bio ?: ""
            originalReceiveMessages = user.receiveMessage
            username = user.username
            originalUsername = user.username
            // Calculate username changeability
            val appConfigResult = appRepository.getAppConfig()
            val cooldownDays = (appConfigResult as? DomainResult.Success)?.data?.usernameChangeCooldownDays ?: 180
            val (canChange, daysRemaining) = viewModel.calculateUsernameChangeability(
                user.usernameChangedAt, cooldownDays
            )
            canChangeUsername = canChange
            daysUntilUsernameChange = daysRemaining
        }
    }

    // Handle photo upload success
    LaunchedEffect(uploadState) {
        if (uploadState is UiState.Success) {
            (userState as? UiState.Success)?.data?.id?.let { userId ->
                onPhotoUploaded?.invoke(userId)
            }
        }
    }

    // Handle save result
    LaunchedEffect(updateProfileState) {
        when (updateProfileState) {
            is UiState.Success -> {
                viewModel.resetUpdateProfileState()
                onNavigateBack()
            }
            is UiState.Error -> {
                val errorMessage = (updateProfileState as UiState.Error).message
                if (errorMessage.contains("already taken", ignoreCase = true)) {
                    usernameValidationError = StringProvider.get("edit_profile_username_taken")
                } else if (errorMessage.contains("cooldown", ignoreCase = true)) {
                    usernameValidationError = StringProvider.get("edit_profile_username_locked")
                        .replace("{days}", daysUntilUsernameChange.toString())
                } else {
                    scope.launch {
                        snackbarHostState.showSnackbar(errorMessage)
                    }
                }
                viewModel.resetUpdateProfileState()
            }
            else -> {}
        }
    }

    DisposableEffect(Unit) {
        onDispose {
            viewModel.resetUploadState()
            viewModel.resetUpdateProfileState()
        }
    }

    fun validateUsername(value: String): String? {
        if (value.length < 3) return StringProvider.get("edit_profile_username_too_short")
        if (value.length > 50) return StringProvider.get("edit_profile_username_too_long")
        if (!value.matches(Regex("^[a-zA-Z0-9_]+$"))) return StringProvider.get("edit_profile_username_invalid")
        return null
    }

    ScreenScaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TovTalkTopBar(
                title = StringProvider.get("edit_profile_title"),
                onBack = onNavigateBack,
            )
        },
        constrainContentWidth = false,
    ) { paddingValues ->
        val editProfileContent = @Composable {
        when (userState) {
            is UiState.Loading, is UiState.Idle, is UiState.KashrutBlocked -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = TovTalkTheme.colors.brand)
                }
            }
            is UiState.Error -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    contentAlignment = Alignment.Center
                ) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Text(
                            (userState as UiState.Error).message,
                            color = TovTalkTheme.colors.error,
                            style = TovTalkTheme.typography.bodyMedium
                        )
                        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
                        TextButton(onClick = { viewModel.loadUser(null) }) {
                            Text(
                                StringProvider.get("common_retry"),
                                color = TovTalkTheme.colors.brand
                            )
                        }
                    }
                }
            }
            is UiState.Success -> {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(paddingValues)
                        .clickable(
                            indication = null,
                            interactionSource = remember { MutableInteractionSource() }
                        ) { editingField = null }
                        .verticalScroll(rememberScrollState())
                        .padding(horizontal = TovTalkTheme.spacing.screenPadding),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Spacer(modifier = Modifier.height(TovTalkTheme.spacing.lg))

                    // === Avatar Section ===
                    Box(
                        modifier = Modifier
                            .size(TovTalkTheme.sizes.avatarXxl)
                            .clip(CircleShape)
                            .background(TovTalkTheme.colors.surfaceOverlay)
                            .clickable { pickPhoto() },
                        contentAlignment = Alignment.Center
                    ) {
                        val imageToShow = (userState as? UiState.Success)?.data?.profilePhoto
                        if (!imageToShow.isNullOrBlank()) {
                            TovTalkAsyncImage(
                                url = imageToShow,
                                modifier = Modifier.fillMaxSize()
                            )
                        } else {
                            Icon(
                                Icons.Default.Person,
                                contentDescription = null,
                                modifier = Modifier.size(TovTalkTheme.sizes.iconXl),
                                tint = TovTalkTheme.colors.textTertiary
                            )
                        }
                        if (uploadState is UiState.Loading) {
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .background(TovTalkTheme.colors.overlayScrim.copy(alpha = TovTalkTheme.opacity.half)),
                                contentAlignment = Alignment.Center
                            ) {
                                CircularProgressIndicator(
                                    progress = { uploadProgress },
                                    modifier = Modifier.size(TovTalkTheme.sizes.iconLg),
                                    color = TovTalkTheme.colors.brand
                                )
                            }
                        }
                        Box(
                            modifier = Modifier
                                .align(Alignment.BottomEnd)
                                .size(TovTalkTheme.sizes.iconMd)
                                .clip(CircleShape)
                                .background(TovTalkTheme.colors.brand),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                Icons.Default.CameraAlt,
                                contentDescription = StringProvider.get("accessibility_change_picture"),
                                modifier = Modifier.size(TovTalkTheme.sizes.iconSm),
                                tint = TovTalkTheme.colors.onBrand
                            )
                        }
                    }
                    TextButton(
                        onClick = { pickPhoto() },
                        enabled = uploadState !is UiState.Loading
                    ) {
                        Text(
                            StringProvider.get("edit_profile_change_photo"),
                            color = TovTalkTheme.colors.brand,
                            style = TovTalkTheme.typography.bodyMedium
                        )
                    }
                    when (uploadState) {
                        is UiState.Success -> {
                            Text(
                                StringProvider.get("edit_profile_photo_success"),
                                color = TovTalkTheme.colors.brand,
                                style = TovTalkTheme.typography.bodySmall
                            )
                        }
                        is UiState.Error -> {
                            Text(
                                (uploadState as UiState.Error).message,
                                color = TovTalkTheme.colors.error,
                                style = TovTalkTheme.typography.bodySmall
                            )
                        }
                        else -> {}
                    }

                    Spacer(modifier = Modifier.height(TovTalkTheme.spacing.lg))

                    // === Personal Details Card ===
                    ProfileCard(
                        title = StringProvider.get("edit_profile_section_personal"),
                        isEditing = editingField == EditField.USERNAME || editingField == EditField.FULLNAME || editingField == EditField.BIO
                    ) {
                        // Username field
                        if (canChangeUsername) {
                            ProfileFieldRow(
                                label = StringProvider.get("edit_profile_username_label"),
                                value = username,
                                isEditing = editingField == EditField.USERNAME,
                                onStartEdit = { editingField = EditField.USERNAME },
                                onValueChange = { newVal ->
                                    if (newVal.length <= 50) {
                                        username = newVal
                                        usernameValidationError = validateUsername(newVal)
                                    }
                                },
                                onDoneEditing = { editingField = null },
                                singleLine = true
                            )
                            usernameValidationError?.let { error ->
                                Text(
                                    text = error,
                                    style = TovTalkTheme.typography.bodySmall,
                                    color = TovTalkTheme.colors.error,
                                    modifier = Modifier.padding(horizontal = TovTalkTheme.spacing.md)
                                )
                            }
                        } else {
                            // Locked username - show disabled
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(
                                        horizontal = TovTalkTheme.spacing.md,
                                        vertical = TovTalkTheme.spacing.sm
                                    ),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(
                                    text = StringProvider.get("edit_profile_username_label"),
                                    style = TovTalkTheme.typography.bodyMedium,
                                    color = TovTalkTheme.colors.textSecondary
                                )
                                Spacer(modifier = Modifier.weight(1f))
                                Text(
                                    text = username,
                                    style = TovTalkTheme.typography.bodyMedium,
                                    color = TovTalkTheme.colors.textDisabled
                                )
                            }
                            Text(
                                text = StringProvider.get("edit_profile_username_locked")
                                    .replace("{days}", daysUntilUsernameChange.toString()),
                                style = TovTalkTheme.typography.bodySmall,
                                color = TovTalkTheme.colors.textTertiary,
                                modifier = Modifier.padding(horizontal = TovTalkTheme.spacing.md)
                            )
                        }
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(1.dp)
                                .background(TovTalkTheme.colors.border.copy(alpha = TovTalkTheme.opacity.subtle))
                        )
                        ProfileFieldRow(
                            label = StringProvider.get("edit_profile_fullname"),
                            value = fullname,
                            isEditing = editingField == EditField.FULLNAME,
                            onStartEdit = { editingField = EditField.FULLNAME },
                            onValueChange = { if (it.length <= 50) fullname = it },
                            onDoneEditing = { editingField = null },
                            singleLine = true
                        )
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(1.dp)
                                .background(TovTalkTheme.colors.border.copy(alpha = TovTalkTheme.opacity.subtle))
                        )
                        ProfileFieldRow(
                            label = StringProvider.get("edit_profile_bio"),
                            value = bio,
                            isEditing = editingField == EditField.BIO,
                            onStartEdit = { editingField = EditField.BIO },
                            onValueChange = { if (it.length <= 150) bio = it },
                            onDoneEditing = { editingField = null },
                            singleLine = false,
                            maxLines = 5,
                            showCharCount = true
                        )
                    }

                    Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))

                    // === Settings Card ===
                    ProfileCard(
                        title = StringProvider.get("edit_profile_section_settings"),
                        isEditing = false
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(
                                    horizontal = TovTalkTheme.spacing.md,
                                    vertical = TovTalkTheme.spacing.sm
                                ),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Column(modifier = Modifier.weight(1f)) {
                                Text(
                                    StringProvider.get("settings_receive_messages"),
                                    style = TovTalkTheme.typography.titleSmall,
                                    color = TovTalkTheme.colors.textPrimary
                                )
                                Text(
                                    StringProvider.get("settings_receive_messages_desc"),
                                    style = TovTalkTheme.typography.bodySmall,
                                    color = TovTalkTheme.colors.textSecondary
                                )
                            }
                            Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xs))
                            Switch(
                                checked = receiveMessages,
                                onCheckedChange = { receiveMessages = it },
                                colors = SwitchDefaults.colors(
                                    checkedThumbColor = TovTalkTheme.colors.onBrand,
                                    checkedTrackColor = TovTalkTheme.colors.brand,
                                    uncheckedThumbColor = TovTalkTheme.colors.textSecondary,
                                    uncheckedTrackColor = TovTalkTheme.colors.surfaceOverlay
                                )
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(TovTalkTheme.spacing.lg))

                    // === Floating Save Button ===
                    AnimatedVisibility(
                        visible = hasChanges,
                        enter = fadeIn(),
                        exit = fadeOut()
                    ) {
                        val isSaving = updateProfileState is UiState.Loading
                        Box(
                            modifier = Modifier
                                .shadow(
                                    elevation = TovTalkTheme.elevation.level3,
                                    shape = RoundedCornerShape(TovTalkTheme.radius.xl)
                                )
                                .clip(RoundedCornerShape(TovTalkTheme.radius.xl))
                                .background(
                                    if (fullname.isBlank()) TovTalkTheme.colors.textDisabled
                                    else TovTalkTheme.colors.brand
                                )
                                .clickable(enabled = !isSaving && fullname.isNotBlank()) {
                                    if (canChangeUsername && username != originalUsername) {
                                        val validationError = validateUsername(username)
                                        if (validationError != null) {
                                            usernameValidationError = validationError
                                            return@clickable
                                        }
                                        viewModel.updateProfile(
                                            fullname = fullname,
                                            bio = bio,
                                            username = username,
                                            receiveMessage = receiveMessages
                                        )
                                    } else {
                                        viewModel.updateProfile(
                                            fullname = fullname,
                                            bio = bio,
                                            receiveMessage = receiveMessages
                                        )
                                    }
                                }
                                .padding(horizontal = TovTalkTheme.spacing.xl, vertical = TovTalkTheme.spacing.xs),
                            contentAlignment = Alignment.Center
                        ) {
                            if (isSaving) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(TovTalkTheme.sizes.iconSm),
                                    color = TovTalkTheme.colors.onBrand,
                                    strokeWidth = TovTalkTheme.spacing.xxxs
                                )
                            } else {
                                Text(
                                    StringProvider.get("edit_profile_save_changes"),
                                    color = TovTalkTheme.colors.onBrand,
                                    style = TovTalkTheme.typography.titleSmall,
                                    fontWeight = FontWeight.SemiBold
                                )
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xxl))
                }
            }
        }
        }
        CenteredContentWrapper { editProfileContent() }
    }
}

// === Helper Composables ===

@Composable
private fun ProfileCard(
    title: String,
    isEditing: Boolean,
    content: @Composable ColumnScope.() -> Unit
) {
    val shape = RoundedCornerShape(TovTalkTheme.radius.md)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .shadow(
                elevation = TovTalkTheme.elevation.card,
                shape = shape
            )
            .then(
                if (isEditing) Modifier
                    .border(1.5.dp, TovTalkTheme.colors.brand, shape)
                else Modifier
            )
            .clip(shape)
            .background(TovTalkTheme.colors.surfaceElevated)
    ) {
        // Section header
        Text(
            text = title,
            style = TovTalkTheme.typography.labelSmall,
            color = TovTalkTheme.colors.brand,
            fontWeight = FontWeight.Bold,
            letterSpacing = 0.5.sp,
            modifier = Modifier.padding(
                start = TovTalkTheme.spacing.md,
                end = TovTalkTheme.spacing.md,
                top = TovTalkTheme.spacing.sm,
                bottom = TovTalkTheme.spacing.xxs
            )
        )
        content()
    }
}

@Composable
private fun ProfileFieldRow(
    label: String,
    value: String,
    isEditing: Boolean,
    onStartEdit: () -> Unit,
    onValueChange: (String) -> Unit,
    onDoneEditing: () -> Unit = {},
    singleLine: Boolean = true,
    maxLines: Int = 1,
    showCharCount: Boolean = false
) {
    if (isEditing) {
        // Edit mode
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(
                    horizontal = TovTalkTheme.spacing.md,
                    vertical = TovTalkTheme.spacing.xs
                )
        ) {
            Text(
                text = label,
                style = TovTalkTheme.typography.labelMedium,
                color = TovTalkTheme.colors.brand,
                fontWeight = FontWeight.Medium
            )
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xxs))
            OutlinedTextField(
                value = value,
                onValueChange = onValueChange,
                modifier = Modifier.fillMaxWidth(),
                singleLine = singleLine,
                maxLines = if (singleLine) 1 else maxLines,
                keyboardOptions = KeyboardOptions(
                    imeAction = if (singleLine) ImeAction.Done else ImeAction.Default
                ),
                keyboardActions = KeyboardActions(
                    onDone = { onDoneEditing() }
                ),
                supportingText = if (showCharCount) {
                    { Text("${value.length}/150", style = TovTalkTheme.typography.bodySmall) }
                } else null,
                colors = TextFieldDefaults.colors(
                    focusedTextColor = TovTalkTheme.colors.textPrimary,
                    unfocusedTextColor = TovTalkTheme.colors.textPrimary,
                    focusedContainerColor = TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.faint),
                    unfocusedContainerColor = TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.faint),
                    focusedIndicatorColor = TovTalkTheme.colors.brand,
                    unfocusedIndicatorColor = TovTalkTheme.colors.brand,
                    cursorColor = TovTalkTheme.colors.brand
                ),
                shape = RoundedCornerShape(TovTalkTheme.radius.sm)
            )
        }
    } else {
        // Display mode
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable { onStartEdit() }
                .padding(
                    horizontal = TovTalkTheme.spacing.md,
                    vertical = TovTalkTheme.spacing.sm
                ),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = label,
                style = TovTalkTheme.typography.bodyMedium,
                color = TovTalkTheme.colors.textSecondary
            )
            Spacer(modifier = Modifier.weight(1f))
            Text(
                text = value.ifBlank { "—" },
                style = TovTalkTheme.typography.bodyMedium,
                color = TovTalkTheme.colors.textPrimary,
                maxLines = if (singleLine) 1 else 2
            )
            Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xxs))
            Icon(
                Icons.Default.Edit,
                contentDescription = null,
                modifier = Modifier.size(TovTalkTheme.sizes.iconXs),
                tint = TovTalkTheme.colors.textTertiary
            )
        }
    }
}
